import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { GridOptions, ColDef, GridApi } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { filter, finalize } from "rxjs/operators";
import { RouterLinkRendererComponent } from '../../../../common/components/ag-grid/router-link-renderer.component';
import { dateComparator } from '../../../../common/components/data-table/comparators/data-table.comparators';
import { ChiefsOrderSearchSpec } from '../../../../models/comodel/chiefs-order-search-spec.model';
import { ChiefsOrderService } from '../../../../services/chiefsOrder.service';
import { ChiefsOrderInstance, chiefsOrderReviewStatus, ChiefsOrderStatus } from '../../../../models/comodel/chiefsorderdetailsmodel.model';
import { GridFormatterService } from '../../../../common/services/grid-formatter.service';
import { DateService } from '../../../../common/services/date.service';
import { LoaderService } from '../../../../common/services/loader.service';
import { TabComponent } from '../../../../common/components/tabs/tab.component';
import { AuthService } from '../../../../services/auth.service';
import { GroupService } from '../../../../admin/services/group.service';
import { UserIdentity } from '../../../../models/user-identity.model';
import { DatePipe } from '@angular/common';
import { DomainDefinitionService } from '../../../../builder/services/domain-definition.service';
import { DomainValue } from '../../../../models/domain.model';

@Component({
    selector: 'co-report',
    templateUrl: './co-report.component.html',
    styleUrls: ['./co-report.component.scss'],
    standalone: false
})
export class ChiefOrdersReportComponent implements OnInit, OnDestroy {

    currentDate: Date = new Date();
    toDate: Date | null;
    fromDate: Date | null;
    searchOngoing: boolean = false;
    searchCriteria: ChiefsOrderSearchSpec = {};
    placeholder: string = DateService.DateFormat;
    mainDisplayPending: boolean = false;
    chiefOrderCategories?: any[];

    columnChiefsOrderNumber: ColDef = {
        headerName: "Chief's Order #",
        headerTooltip: "Chief's Order #",
        field: "chiefsOrderNumber",
        tooltipField: 'chiefsOrderNumber',
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            enableLink: this.enableChiefsOrderNumberLink(),
            openInNewWindow: true,
            textField: "chiefsOrderNumber",
            url: (data: ChiefsOrderInstance) => {
                let url = `${this.router.parseUrl(this.router.url).root}/admin/chief-orders/co-details/${data.id}`
                return url;
            }
        },
        minWidth: 125,
        maxWidth: 150
    };

    columnApplicationNumber: ColDef = {
        headerName: 'Application #',
        headerTooltip: 'Application #',
        field: 'applicationNumber',
        tooltipField: 'applicationNumber',
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            enableLink: this.enableApplicationNumberLink(),
            idField: 'id',
            textField: 'applicationNumber',
            url: this.linkFormatter(),
        },
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
        maxWidth: 170,
        minWidth: 150
    };

    columnAssignee: ColDef = {
        headerName: 'Assigned To',
        headerTooltip: 'Assigned To',
        field: 'assignedToGroup',
        tooltipField: 'assignedToGroup',
        tooltipValueGetter: this.assignedToFormatter(),
        valueGetter: this.assignedToFormatter(),
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
        maxWidth: 175,
        minWidth: 100
    };

   categoryOrgName: ColDef = {
        headerName: 'Category',
        headerTooltip: 'Category',
        valueGetter: this.formatCategoryDomainFieldsType(),
        tooltipValueGetter: this.formatCategoryDomainFieldsType(),
        maxWidth: 150,
        minWidth: 125
    };

    columnOrgName: ColDef = {
        headerName: 'Owner Name',
        headerTooltip: 'Owner Name',
        field: "organizationName",
        tooltipField: "organizationName",
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
        maxWidth: 200,
        minWidth: 150
    };

    columnOrgId: ColDef = {
        headerName: 'Owner Number',
        headerTooltip: 'Owner Number',
        field: "organizationId",
        tooltipField: "organizationId",
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
        maxWidth: 150,
        minWidth: 125
    };

    columnDateSubmitted: ColDef = {
        headerName: 'Initiated Date',
        headerTooltip: 'Initiated Date',
        field: 'initiationTime',
        tooltipValueGetter: this.formatterService.formatDate('initiationTime'),
        valueGetter: this.formatterService.formatDate('initiationTime'),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatterService.defaultDateFilterParams,
        minWidth: 125,
        maxWidth: 150
    };

    columnTemplateName: ColDef = {
        headerName: 'Type',
        headerTooltip: 'Type',
        field: 'reportTemplateName',
        tooltipField: 'reportTemplateName',
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
        minWidth: 150,
        maxWidth: 175
    };

    columnReviewStatus: ColDef = {
        headerName: 'Review Status',
        headerTooltip: 'Review Status',
        field: 'reviewStatus',
        tooltipField: 'reviewStatus',
        minWidth: 150,
        maxWidth: 175
    };

    columnStatus: ColDef = {
        headerName: 'Status',
        headerTooltip: 'Status',
        field: 'status',
        tooltipField: 'status',
        minWidth: 125,
        maxWidth: 175
    };

    columnDateLastUpdated: ColDef = {
        headerName: 'Date Last Updated',
        headerTooltip: 'Date Last Updated',
        field: 'lastUpdatedTime',
        tooltipValueGetter: this.formatterService.formatDate('lastUpdatedTime'),
        valueGetter: this.formatterService.formatDate('lastUpdatedTime'),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatterService.defaultDateFilterParams,
        minWidth: 150,
        maxWidth: 200
    };

    columnDateIssued: ColDef = {
        headerName: 'Date Issued',
        headerTooltip: 'Date Issued',
        field: 'issueDate',
        tooltipValueGetter: this.formatterService.formatDate('issueDate'),
        valueGetter: this.formatterService.formatDate('issueDate'),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatterService.defaultDateFilterParams,
        minWidth: 200,
        maxWidth: 250
    };

    columnDateLetterSent: ColDef = {
        headerName: 'Date Letter Mailed',
        headerTooltip: 'Date Letter Mailed',
        field: 'letterMailedDate',
        tooltipValueGetter: this.formatterService.formatDate('letterMailedDate'),
        valueGetter: this.formatterService.formatDate('letterMailedDate'),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatterService.defaultDateFilterParams,
        minWidth: 200,
        maxWidth: 250
    };

    columnDateLetterDelivered: ColDef = {
        headerName: 'Effective Date',
        headerTooltip: 'Effective Date',
        field: 'letterDeliveryDate',
        tooltipValueGetter: this.formatterService.formatDate('letterDeliveryDate'),
        valueGetter: this.formatterService.formatDate('letterDeliveryDate'),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatterService.defaultDateFilterParams,
        minWidth: 200,
        maxWidth: 250
    };

    columnDocType: ColDef = {
        headerName: 'OnBase DocType',
        headerTooltip: 'OnBase DocType',
        field: "onBaseDocumentTypeName",
        tooltipField: "onBaseDocumentTypeName",
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
        minWidth: 250,
        maxWidth: 350
    };

    subscriptions: Subscription = new Subscription();
    allData: ChiefsOrderInstance[];
    data: ChiefsOrderInstance[];
    filteredData: ChiefsOrderInstance[];
	gridOptions: GridOptions;
	gridApi?: GridApi;
	filteredGridOptions: GridOptions; 
	filteredGridApi?: GridApi;

    public columns = [this.columnDateSubmitted, this.columnApplicationNumber, this.columnChiefsOrderNumber, this.columnDateLastUpdated, this.columnOrgName, this.columnOrgId, this.categoryOrgName,
        this.columnTemplateName, this.columnStatus, this.columnReviewStatus, this.columnAssignee, this.columnDateIssued, this.columnDateLetterSent,
        this.columnDateLetterDelivered, this.columnDocType];

    constructor(
        public formatterService: GridFormatterService,
        public chiefsOrderService: ChiefsOrderService,
        public readonly dateService: DateService,
        public domainService: DomainDefinitionService,
        private router: Router,
        public readonly loader: LoaderService,
        public authService: AuthService,
        public groupService: GroupService,
        private datePipe: DatePipe
    ) { }

    ngOnInit() {
        this.gridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.columns, false, true, true, true);
		this.gridOptions.overlayLoadingTemplate = '<span class="ag-overlay-loading-center">Please wait ...</span>';
		this.gridOptions.onGridReady = (params) => { 
            this.gridApi = params.api; 
            this.autoSizeGeneralGrid(params);
        };
        this.filteredGridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.columns, false, true, true, true);
        this.filteredGridOptions.overlayLoadingTemplate = '<span class="ag-overlay-loading-center">Please wait ...</span>';
		this.filteredGridOptions.onGridReady = (params) => { 
            this.filteredGridApi = params.api; 
            this.autoSizeMyOrdersGrid(params);
        };
        this.setDefaultDates();

        var dService$ = this.domainService.get('ChiefsOrderCategory')
            .pipe(
                finalize(() => {
                    this.subscriptions.add(dService$);
                })
            )
            .subscribe((data: any) => {
                this.chiefOrderCategories = data.values;
            });

        this.searchCriteria.issuedDateTo = this.toDate;
        this.searchCriteria.issuedDateFrom = this.fromDate;
        this.search();

        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.subscriptions.unsubscribe();
        });
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    public enableApplicationNumberLink() {
        return (obj: any) => {
            let chiefsOrderInstance = obj as ChiefsOrderInstance;
            return (chiefsOrderInstance.reviewStatus != chiefsOrderReviewStatus.Completed);
        }
    }

    public enableChiefsOrderNumberLink() {
        return (obj: any) => {
            let chiefsOrderInstance = obj as ChiefsOrderInstance;
            return (chiefsOrderInstance.reviewStatus == chiefsOrderReviewStatus.Completed);
        }
    }

    linkFormatter() {
        return (obj: any) => {
            let link = "";
            let previousLevel = obj.previousLevelNumber;
            if (previousLevel != null && previousLevel != "" && previousLevel != undefined)
                link = '/admin/chief-orders/co-review-chiefsorder/';
            else
                link = '/admin/chief-orders/co-approve-letter/';

            return link;
        };
    }

    assignedToFormatter(): any {
        return (obj: any) => {
            let chiefsOrderInstance = obj.data as ChiefsOrderInstance;
            let assignedTo = (chiefsOrderInstance.reviewStatus != chiefsOrderReviewStatus.Completed) ? chiefsOrderInstance.assignedToGroup : "N/A"
            return assignedTo;
        };
    }

    search(): void {
        if (!this.searchOngoing) {
            this.searchOngoing = true;
            this.allData = [];
            this.data = [];
            this.loader.toggleLoader(true);
            let chiefsOrders$ = this.chiefsOrderService.getChiefsOrderInstances(this.searchCriteria).pipe(finalize(() => {
                this.subscriptions.add(chiefsOrders$);
            })).subscribe({
                next: (matchingChiefsOrders) => {
                    this.searchOngoing = false;
                    this.loader.toggleLoader(false);

                    this.allData = matchingChiefsOrders.sort((a, b) => a.issueDate > b.issueDate ? 1 : -1);
                    this.data = this.mainDisplayPending ? this.allData : this.allData.filter(a => a.status != ChiefsOrderStatus.Pending);
                    this.filteredData = this.allData.filter(a => a.status == ChiefsOrderStatus.Pending  && this.user.groups.filter(b => b == a.assignedToGroupId).length > 0)

                    this.onTabClicked(null);
                }, 
                error: (error) => {
                    this.searchOngoing = false;
                    this.loader.toggleLoader(false);
                    console.error(error)
                }
            });
        }
    }

    reset(): void {
        this.setDefaultDates();
        this.searchCriteria.issuedDateTo = this.toDate;
        this.searchCriteria.issuedDateFrom = this.fromDate;
        this.search();
    }

    goHome(): void {
        this.router.navigate(["home"]);
    }

    get user(): UserIdentity {
        return this.authService.User;
    }

    onTabClicked(tab: TabComponent | null): void {
        if ((tab == null || tab.title == "All Chief's Orders") && this.gridApi) {
            this.autoSizeGeneralGrid(null);
        }
        if ((tab == null || tab.title == "My Chief's Orders") && this.filteredGridApi) {
            this.autoSizeMyOrdersGrid(null);
        }
    }


    autoSizeGeneralGrid(event: any) {
        setTimeout(() => {
            if (this.gridApi != null) {
                this.gridApi.autoSizeAllColumns();
            }
            
        }, 500);
    }


    autoSizeMyOrdersGrid(event: any) {
        setTimeout(() => {
            if (this.filteredGridApi != null) {
                this.filteredGridApi.autoSizeAllColumns();
            }
        }, 500);
    }

    setDefaultDates(): void {
        this.fromDate = new Date();
        this.fromDate.setDate(this.currentDate.getDate() - 16);
        this.toDate = this.currentDate;
    }

    fromDateChange(evt: Date | null): void {
        this.fromDate = evt;
        this.searchCriteria.issuedDateFrom = this.fromDate;
    }

    toDateChange(evt: Date | null): void {
        this.toDate = evt;
        if (this.toDate != null) {
            this.toDate.setHours(23);
            this.toDate.setMinutes(59);
            this.toDate.setSeconds(59);
            this.toDate.setMilliseconds(999);
        }        
        this.searchCriteria.issuedDateTo = this.toDate;
    }

    downloadReport() {
        const params = {
            skipHeader: false,
            allColumns: true,
            fileName: this.formatFileName(),
            sheetName: "Chief's Orders",
            exportMode: "xlsx"
        }
        this.formatterService.exportDataToExcel(params.fileName, params.sheetName, this.gridApi);
    }

    formatFileName(): string {
        let fileName;
        if (!this.fromDate || !this.toDate) {
            const today = this.datePipe.transform(new Date(), "yyyy-MM-dd");
            fileName = `ChiefsOrders_${today}`;
        } else {
            const fromDate = this.datePipe.transform(this.fromDate, "yyyy-MM-dd");
            const toDate = this.datePipe.transform(this.toDate, "yyyy-MM-dd");
            fileName = `ChiefsOrders_${fromDate}_${toDate}`;
        }
        return fileName;
    }

    toggleMainTabPendingItemDisplay() {
        this.mainDisplayPending = !this.mainDisplayPending;

        this.data = this.mainDisplayPending ? this.allData : this.allData.filter(a => a.status != ChiefsOrderStatus.Pending);
		if (this.gridApi != null) {
            this.gridApi.setGridOption("rowData", this.data);
        }
    }

    formatCategoryDomainFieldsType() {
        return (obj: any) => {
            if (obj.data) {
                var path = "$.categoryCode";
                let fieldValue = this.formatterService.tryGetValue(obj.data, path);
                let domainValues: any;
                domainValues = this.chiefOrderCategories;

                if (domainValues && domainValues.length > 0) {

                    let matchingDomainValue = domainValues.filter((a: any) => a.Code == fieldValue);
                    if (matchingDomainValue.length > 0) {
                        let domainValueToReturn = matchingDomainValue[0].Definition;
                        return domainValueToReturn;
                    }
                    else
                        return fieldValue;
                }
                else {
                    return fieldValue;
                }
            }
            else return "";
        }
    }
}
